import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Flex,
  Form,
  InputNumber,
  List,
  Row,
  Skeleton,
  Tag,
  Typography,
  message,
  theme,
} from 'antd';
import {
  BankOutlined,
  CheckCircleOutlined,
  FileAddOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { UpdateCollaborationDocument } from '../helper/apiHelper';
import type {
  OrganizationRegistration,
  ReducedOrganization,
  StudyYear,
} from '../Types/interfaces';
import { CollaborationType } from '../Types/interfaces';
import OrganizationElementComponent from './Organization/OrganizationElementComponent';
import { useCurrentStudyYear } from '../hooks/useStudyYear';
import { useOrganizationsRegistrationsByOrganizationId } from '../hooks/useOrganizations';
import { QUERY_KEYS } from '../Constants/queryKeys';
import { formatDate } from '../Utils/date';

interface OrganizationRegistrationProcessComponentProps {
  userOrganization: ReducedOrganization;
}

interface StudentCountForm {
  bachelorStudentCount: number;
  masterStudentCount: number;
}

const studyYearRange = (studyYear: StudyYear | undefined) => ({
  start: formatDate(studyYear?.startDate),
  end: formatDate(studyYear?.endDate),
});

// University organizations are part of UBB: no yearly collaboration agreement
const UniversityOrganizationOverview = ({
  organization,
  currentStudyYear,
}: {
  organization: ReducedOrganization;
  currentStudyYear: StudyYear | undefined;
}): React.ReactElement => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  return (
    <Flex vertical gap={20}>
      <Flex gap={16} align="flex-start">
        <Flex
          align="center"
          justify="center"
          style={{
            flex: '0 0 56px',
            height: 56,
            borderRadius: '50%',
            background: token.colorPrimaryBg,
            color: token.colorPrimary,
            fontSize: 26,
          }}
        >
          <BankOutlined />
        </Flex>
        <Flex vertical>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t('orgRegistrationCard.uniTitle')}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('orgRegistrationCard.uniDescription', {
              name: organization.name,
            })}
          </Typography.Paragraph>
        </Flex>
      </Flex>

      <Descriptions
        size="small"
        bordered
        column={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2, xxl: 2 }}
      >
        <Descriptions.Item label={t('orgRegistrationCard.uniCurrentStudyYear')}>
          {currentStudyYear
            ? t(
                'orgRegistrationCard.studyYear',
                studyYearRange(currentStudyYear)
              )
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('orgRegistrationCard.uniAgreement')}>
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {t('orgRegistrationCard.uniNotRequired')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('orgRegistrationCard.uniOffers')}>
          {t('orgRegistrationCard.uniOffersValue')}
        </Descriptions.Item>
        <Descriptions.Item label={t('orgRegistrationCard.uniWorkSafetyLabel')}>
          {t('orgRegistrationCard.uniWorkSafety')}
        </Descriptions.Item>
      </Descriptions>
    </Flex>
  );
};

// Shown to partner organizations when a new study year has no agreement yet
const NewStudyYearRegistration = ({
  organization,
  currentStudyYear,
}: {
  organization: ReducedOrganization;
  currentStudyYear: StudyYear | undefined;
}): React.ReactElement => {
  const { t } = useTranslation();
  const [form] = Form.useForm<StudentCountForm>();
  const [messageApi, contextHolder] = message.useMessage();
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const handleDocumentUpdate = async (
    values: StudentCountForm
  ): Promise<void> => {
    setSaving(true);
    try {
      await UpdateCollaborationDocument(organization.id.toString(), {
        studyYearId: currentStudyYear?.id ?? 0,
        registrationDate: '',
        registrationNumber: '',
        collaborationType: CollaborationType.AnualAgreement,
        bachelorStudentCount: values.bachelorStudentCount,
        masterStudentCount: values.masterStudentCount,
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CURRENT_STUDY_YEAR,
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.ORGANIZATION_REGISTRATIONS, organization.id],
      });
    } catch (error) {
      console.error(error);
      messageApi.error(t('orgRegistrationCard.generateError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Flex vertical gap={16}>
      {contextHolder}
      <Alert
        type="warning"
        showIcon
        message={t('orgRegistrationCard.newYearTitle')}
        description={t(
          'orgRegistrationCard.newYearDescription',
          studyYearRange(currentStudyYear)
        )}
      />
      <Card
        type="inner"
        size="small"
        title={t('orgRegistrationCard.newYearFormTitle')}
      >
        <Form form={form} layout="vertical" onFinish={handleDocumentUpdate}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="bachelorStudentCount"
                label={t('orgElementComp.bachelorCount')}
                rules={[
                  { required: true, message: t('orgElementComp.numberNeeded') },
                ]}
              >
                <InputNumber min={0} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="masterStudentCount"
                label={t('orgElementComp.masterCount')}
                rules={[
                  { required: true, message: t('orgElementComp.numberNeeded') },
                ]}
              >
                <InputNumber min={0} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Flex justify="flex-end">
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              icon={<FileAddOutlined />}
            >
              {t('orgElementComp.updateDocument')}
            </Button>
          </Flex>
        </Form>
      </Card>
    </Flex>
  );
};

export const OrganizationRegistrationProcessComponent = ({
  userOrganization,
}: OrganizationRegistrationProcessComponentProps): React.ReactElement => {
  const { t } = useTranslation();
  const { data: currentStudyYear, isLoading: isStudyYearLoading } =
    useCurrentStudyYear();
  const { data: organizationRegistrations, isLoading: isRegistrationsLoading } =
    useOrganizationsRegistrationsByOrganizationId(userOrganization.id);

  const { currentRegistration, archivedRegistrations } = useMemo(() => {
    const registrations: OrganizationRegistration[] =
      organizationRegistrations ?? [];
    const current =
      registrations.find(
        (registration) =>
          registration.collaborationDocument.studyYearId ===
          currentStudyYear?.id
      ) ?? null;

    return {
      currentRegistration: current,
      archivedRegistrations: registrations.filter(
        (registration) => registration !== current
      ),
    };
  }, [organizationRegistrations, currentStudyYear]);

  const isUniversity = userOrganization.isUniversityOrganization;

  const renderBody = (): React.ReactNode => {
    if (isStudyYearLoading || isRegistrationsLoading) {
      return <Skeleton active paragraph={{ rows: 3 }} />;
    }
    if (isUniversity) {
      return (
        <UniversityOrganizationOverview
          organization={userOrganization}
          currentStudyYear={currentStudyYear}
        />
      );
    }
    if (currentRegistration) {
      return (
        <OrganizationElementComponent
          isArchived={false}
          userOrganization={userOrganization}
          organizationRegistration={currentRegistration}
        />
      );
    }
    return (
      <NewStudyYearRegistration
        organization={userOrganization}
        currentStudyYear={currentStudyYear}
      />
    );
  };

  return (
    <Card
      title={
        <Typography.Text strong style={{ fontSize: 18 }}>
          {userOrganization.name}
        </Typography.Text>
      }
      extra={
        isUniversity ? (
          <Tag color="blue" icon={<BankOutlined />}>
            {t('orgRegistrationCard.universityOrganization')}
          </Tag>
        ) : (
          <Tag icon={<ShopOutlined />}>
            {t('orgRegistrationCard.partnerOrganization')}
          </Tag>
        )
      }
    >
      <Flex vertical gap={16}>
        {renderBody()}

        {archivedRegistrations.length > 0 && (
          <Collapse
            ghost
            items={[
              {
                key: 'archive',
                label: t('orgRegistrationCard.previousYears', {
                  count: archivedRegistrations.length,
                }),
                children: (
                  <List
                    size="small"
                    dataSource={archivedRegistrations}
                    renderItem={(registration) => (
                      <List.Item key={registration.id}>
                        <OrganizationElementComponent
                          isArchived
                          userOrganization={userOrganization}
                          organizationRegistration={registration}
                        />
                      </List.Item>
                    )}
                  />
                ),
              },
            ]}
          />
        )}
      </Flex>
    </Card>
  );
};
